import React, { useEffect } from "react";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";
import { ISubAccount } from "./types";

// Footer and other page overlays already use z-index 1000+, so the backdrop must sit above them.
const OVERLAY_CLASSES =
  "fixed inset-0 z-[10100] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm";

interface RemoveSubAccountModalProps {
  account: ISubAccount;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function RemoveSubAccountModal({
  account,
  isDeleting,
  onCancel,
  onConfirm,
}: RemoveSubAccountModalProps) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !isDeleting) {
        onCancel();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDeleting, onCancel]);

  return (
    <div
      className={OVERLAY_CLASSES}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isDeleting) {
          onCancel();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-access-title"
        aria-describedby="remove-access-description"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-[#E2E8F0] px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </span>
            <div>
              <h2
                id="remove-access-title"
                className="text-lg font-bold text-[#2D3748]"
              >
                Remover acesso?
              </h2>
              <p className="mt-0.5 text-xs text-[#64748B]">
                Esta ação não pode ser desfeita.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            aria-label="Fechar modal"
            className="rounded-full p-2 text-[#64748B] transition-colors hover:bg-[#F1F5F9] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p id="remove-access-description" className="text-sm leading-6 text-[#475569]">
            O e-mail <strong className="font-semibold text-[#2D3748]">{account.email}</strong>{" "}
            perderá o acesso aos imóveis vinculados.
          </p>

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
            Caso este e-mail já tivesse uma conta no sistema, a conta será
            preservada e apenas o vínculo de acesso será removido.
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 bg-[#F8FAFC] px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-full border border-[#CBD5E1] px-5 py-2.5 text-sm font-semibold text-[#475569] transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {isDeleting ? "Removendo..." : "Remover acesso"}
          </button>
        </div>
      </section>
    </div>
  );
}
